import React, { useMemo, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalComplianceApi from "../services/portalComplianceApi";
import { getStoredPermissions } from "../services/authApi";
import "../styles/PortalBusiness.css";

function fmtDateTime(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

export default function PortalComplianceDashboardPage() {
  const perms = getStoredPermissions();

  const canOverview = perms.includes("compliance.read");
  const canVerifications = perms.includes("compliance.verifications.read");
  const canRisk = perms.includes("compliance.risk.read");
  const canShipments = perms.includes("compliance.shipments.read");

  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "Shipments", to: "/portal/shipments", perm: "logistics.shipments.read" },
      { label: "Metered", to: "/portal/metered", perm: "meter.session.read" },
      { label: "Forecast", to: "/portal/forecast", perm: "analytics.forecast.read" },
      { label: "Compliance", to: "/portal/compliance", perm: "compliance.read" },
    ],
    []
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [filters, setFilters] = useState({ from: "", to: "", branchId: "" });

  const [overview, setOverview] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [riskEvents, setRiskEvents] = useState([]);
  const [shipmentRows, setShipmentRows] = useState([]);

  async function loadAll() {
    setLoading(true);
    setErr("");
    try {
      const params = {
        from: filters.from || undefined,
        to: filters.to || undefined,
        branchId: filters.branchId ? Number(filters.branchId) : undefined,
      };

      if (canOverview) {
        const o = await portalComplianceApi.getOverview(params);
        if (!o?.ok) throw new Error(o?.error || "Failed to load overview");
        setOverview(o);
      } else {
        setOverview(null);
      }

      if (canVerifications) {
        const v = await portalComplianceApi.getVerifications({ ...params, groupBy: "day" });
        if (!v?.ok) throw new Error(v?.error || "Failed to load verifications");
        setVerifications(Array.isArray(v.rows) ? v.rows : []);
      } else {
        setVerifications([]);
      }

      if (canRisk) {
        const r = await portalComplianceApi.listRiskEvents({ ...params, limit: 50, offset: 0 });
        if (!r?.ok) throw new Error(r?.error || "Failed to load risk events");
        setRiskEvents(Array.isArray(r.rows) ? r.rows : []);
      } else {
        setRiskEvents([]);
      }

      if (canShipments) {
        const s = await portalComplianceApi.listShipments({ ...params, limit: 50, offset: 0 });
        if (!s?.ok) throw new Error(s?.error || "Failed to load shipment compliance");
        setShipmentRows(Array.isArray(s.rows) ? s.rows : []);
      } else {
        setShipmentRows([]);
      }
    } catch (e) {
      setErr(e?.message || "Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  }

  const noSections =
    !canOverview && !canVerifications && !canRisk && !canShipments;

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Compliance"
          subtitle="Verification trends, risk events, and shipment compliance (permission-gated)"
          links={links}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Compliance Dashboard</h1>
              <p className="ucx-portal-sub">
                Use filters to scope the dataset. Sections render only if your role includes the matching compliance permissions.
              </p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-primary" type="button" onClick={loadAll} disabled={loading}>
                Load
              </button>
            </div>
          </div>

          {err ? <div className="ucx-portal-error">{err}</div> : null}
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          <div className="ucx-portal-card-large">
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Filters</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {canOverview ? <span className="ucx-portal-badge ucx-portal-badge-success">Overview</span> : <span className="ucx-portal-badge">Overview</span>}
                {canVerifications ? <span className="ucx-portal-badge ucx-portal-badge-success">Verifications</span> : <span className="ucx-portal-badge">Verifications</span>}
                {canRisk ? <span className="ucx-portal-badge ucx-portal-badge-success">Risk</span> : <span className="ucx-portal-badge">Risk</span>}
                {canShipments ? <span className="ucx-portal-badge ucx-portal-badge-success">Shipments</span> : <span className="ucx-portal-badge">Shipments</span>}
              </div>
            </div>

            <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              <div className="ucx-portal-field">
                <label>From (YYYY-MM-DD)</label>
                <input
                  value={filters.from}
                  onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                  placeholder="e.g. 2025-12-01"
                />
              </div>

              <div className="ucx-portal-field">
                <label>To (YYYY-MM-DD)</label>
                <input
                  value={filters.to}
                  onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                  placeholder="e.g. 2025-12-31"
                />
              </div>

              <div className="ucx-portal-field">
                <label>Branch ID (optional)</label>
                <input
                  value={filters.branchId}
                  onChange={(e) => setFilters((p) => ({ ...p, branchId: e.target.value }))}
                  placeholder="e.g. 1"
                />
              </div>

              <div className="ucx-portal-field" style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
                <button className="ucx-btn ucx-btn-primary" type="button" onClick={loadAll} disabled={loading}>
                  Apply
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  type="button"
                  onClick={() => {
                    setFilters({ from: "", to: "", branchId: "" });
                    setErr("");
                    setOverview(null);
                    setVerifications([]);
                    setRiskEvents([]);
                    setShipmentRows([]);
                  }}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
            </div>

            {noSections ? (
              <div className="ucx-portal-warn danger" style={{ marginTop: 10 }}>
                Your account has portal access, but no compliance modules are assigned. Ask admin to grant compliance permissions.
              </div>
            ) : null}
          </div>

          {canOverview && overview ? (
            <div className="ucx-portal-grid" style={{ marginTop: 14 }}>
              <div className="ucx-portal-card-large">
                <div className="ucx-portal-card-label">Verifications (total)</div>
                <div className="ucx-portal-card-value">{overview.verifications?.total ?? 0}</div>

                <div className="ucx-portal-tablewrap" style={{ marginTop: 12 }}>
                  <table className="ucx-portal-table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Result</th>
                        <th style={{ textAlign: "right" }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overview.verifications?.byResult || []).length ? (
                        (overview.verifications.byResult || []).map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.result}</td>
                            <td style={{ textAlign: "right" }}>{r.count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="ucx-muted">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ucx-portal-card-large">
                <div className="ucx-portal-card-label">Risk events (total)</div>
                <div className="ucx-portal-card-value">{overview.riskEvents?.total ?? 0}</div>

                <div className="ucx-portal-tablewrap" style={{ marginTop: 12 }}>
                  <table className="ucx-portal-table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Severity</th>
                        <th style={{ textAlign: "right" }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overview.riskEvents?.bySeverity || []).length ? (
                        (overview.riskEvents.bySeverity || []).map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.severity}</td>
                            <td style={{ textAlign: "right" }}>{r.count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="ucx-muted">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ucx-portal-card-large">
                <div className="ucx-portal-card-label">Shipments (total)</div>
                <div className="ucx-portal-card-value">{overview.shipments?.total ?? 0}</div>

                <div className="ucx-portal-tablewrap" style={{ marginTop: 12 }}>
                  <table className="ucx-portal-table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th style={{ textAlign: "right" }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overview.shipments?.byStatus || []).length ? (
                        (overview.shipments.byStatus || []).map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.status}</td>
                            <td style={{ textAlign: "right" }}>{r.count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="ucx-muted">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}

          {canVerifications ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Verifications by day</div>
                <div className="ucx-portal-toolbar-hint">Grouped by day and result.</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Result</th>
                      <th style={{ textAlign: "right" }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(verifications || []).length ? (
                      verifications.map((r, idx) => (
                        <tr key={idx}>
                          <td>{r.day}</td>
                          <td>{r.result}</td>
                          <td style={{ textAlign: "right" }}>{r.count}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="ucx-muted">
                          No rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {canRisk ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Latest risk events</div>
                <div className="ucx-portal-toolbar-hint">Most recent 50.</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Module</th>
                      <th>Code</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(riskEvents || []).length ? (
                      riskEvents.map((r) => (
                        <tr key={r.id}>
                          <td>{fmtDateTime(r.created_at)}</td>
                          <td>{r.severity}</td>
                          <td>{r.module}</td>
                          <td>{r.code}</td>
                          <td>{r.description}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="ucx-muted">
                          No rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {canShipments ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Shipments (compliance view)</div>
                <div className="ucx-portal-toolbar-hint">Most recent 50.</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 980 }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Status</th>
                      <th>Pending</th>
                      <th>Origin</th>
                      <th>Destination</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(shipmentRows || []).length ? (
                      shipmentRows.map((s) => (
                        <tr key={s.id}>
                          <td>{s.id}</td>
                          <td>{s.status}</td>
                          <td>
                            {s.pending_status || "-"} {s.pending_action ? `(${s.pending_action})` : ""}
                          </td>
                          <td>{s.origin_branch_id ?? "-"}</td>
                          <td>{s.destination_branch_id ?? "-"}</td>
                          <td>{fmtDateTime(s.created_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="ucx-muted">
                          No rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
